import type { TProduct } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import {
  Alert,
  Avatar,
  Button,
  Card,
  Collapse,
  ColorSwatch,
  Container,
  Group,
  SimpleGrid,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { IconSearch } from "@tabler/icons-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { CardGridSkeleton, PageHeaderSkeleton } from "../../../components/PageSkeletons"
import { api } from "../../../lib/api"
import { extractErrorMessage, unwrapData } from "../../../lib/api/errors"
import { formatDate } from "../../../lib/format-date"
import { notifyError } from "../../../lib/notify"
import { queryKeys } from "../../../lib/query-keys"
import { useProduct } from "../../../lib/tenant"

interface ICompany {
  id: string
  name: string
  slug: string
  logo: string | null
  product: TProduct
  metadata?: { brandColor?: string; loginBackground?: string; customCss?: string }
  createdAt: string
  memberCount: number
  meetingsLast30d: number
}

export function CompaniesPage(): React.JSX.Element {
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [search, setSearch] = useState("")
  const queryClient = useQueryClient()
  const product = useProduct()
  const { i18n } = useLingui()

  const {
    data: companies = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.admin.companies(),
    queryFn: async () => {
      const result = await api.api.admin.companies.get()
      // The list endpoint types name/slug/logo/product/createdAt as t.Unknown() on
      // the wire; narrow the row shape to the page's ICompany here.
      return unwrapData(result) as unknown as ICompany[]
    },
  })

  const { mutate: createCompany, isPending: creating } = useMutation({
    mutationFn: async () => {
      const result = await api.api.admin.companies.post({
        name,
        slug: slug || name.toLowerCase().replace(/\s+/g, "-"),
      })
      return unwrapData(result)
    },
    onSuccess: () => {
      setName("")
      setSlug("")
      setShowCreate(false)
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.companies() })
    },
    onError: (err) => {
      notifyError(err, i18n._(msg`Failed to create company`))
    },
  })

  const handleCreate = (e: React.FormEvent): void => {
    e.preventDefault()
    createCompany()
  }

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (needle === "") return companies
    return companies.filter(
      (company) => company.name.toLowerCase().includes(needle) || company.slug.toLowerCase().includes(needle),
    )
  }, [companies, search])

  if (isLoading) {
    return (
      <Container size="md">
        <PageHeaderSkeleton />
        <CardGridSkeleton count={6} />
      </Container>
    )
  }

  if (error) {
    return (
      <Alert color="danger" variant="light">
        {extractErrorMessage(error)}
      </Alert>
    )
  }

  return (
    <Container size="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>{product === "voxcanon" ? <Trans>Gaming Groups</Trans> : <Trans>Companies</Trans>}</Title>
        <Button onClick={() => setShowCreate(!showCreate)} variant={showCreate ? "subtle" : "filled"}>
          {showCreate ? <Trans>Cancel</Trans> : <Trans>New company</Trans>}
        </Button>
      </Group>

      <Collapse in={showCreate}>
        <Card withBorder mb="lg" p="md">
          <form onSubmit={handleCreate}>
            <Group grow>
              <TextInput
                placeholder={i18n._(msg`Company name`)}
                value={name}
                onChange={(e) => {
                  setName(e.currentTarget.value)
                  if (!slug) setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))
                }}
                required
                autoFocus
              />
              <TextInput
                placeholder={i18n._(msg`Slug (subdomain)`)}
                value={slug}
                onChange={(e) => setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                required
              />
              <Button type="submit" loading={creating}>
                <Trans>Create</Trans>
              </Button>
            </Group>
          </form>
        </Card>
      </Collapse>

      {companies.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          <Trans>No companies yet. Create one to get started.</Trans>
        </Text>
      ) : (
        <>
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={i18n._(msg`Search companies`)}
            aria-label={i18n._(msg`Search companies`)}
            leftSection={<IconSearch size={16} aria-hidden />}
            maw={360}
            mb="md"
          />
          {visible.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              <Trans>No companies match.</Trans>
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {visible.map((c) => (
                <Card
                  key={c.id}
                  component="a"
                  href={`/companies/${c.id}`}
                  withBorder
                  padding="lg"
                  style={{ textDecoration: "none", cursor: "pointer" }}
                >
                  <Group justify="space-between">
                    <Group gap="sm">
                      {c.logo ? (
                        <Avatar src={c.logo} size="md" radius="sm" />
                      ) : (
                        <Avatar size="md" radius="sm" color="brand">
                          {c.name.charAt(0)}
                        </Avatar>
                      )}
                      <div>
                        <Text fw={600}>{c.name}</Text>
                        <Text size="xs" c="dimmed">
                          {c.slug}.{c.product === "voxcanon" ? "voxcanon" : "lexicanon"}.com
                        </Text>
                      </div>
                    </Group>
                    <Group gap="xs">
                      {typeof c.metadata?.brandColor === "string" && c.metadata.brandColor.trim() !== "" ? (
                        <Tooltip label={i18n._(msg`Brand colour`)}>
                          <span role="img" aria-label={i18n._(msg`Brand colour`)}>
                            <ColorSwatch color={c.metadata.brandColor} size={16} aria-hidden />
                          </span>
                        </Tooltip>
                      ) : null}
                    </Group>
                  </Group>
                  <Text size="xs" c="dimmed" mt="sm">
                    <Trans>Created {formatDate(i18n, c.createdAt)}</Trans>
                    {" · "}
                    <Plural value={c.memberCount ?? 0} one="# member" other="# members" />
                    {" · "}
                    <Plural
                      value={c.meetingsLast30d ?? 0}
                      one="# meeting in the last 30 days"
                      other="# meetings in the last 30 days"
                    />
                  </Text>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </>
      )}
    </Container>
  )
}
